import React, { useState, useEffect, useCallback, useContext } from 'react';
import { getUsers, updateUserStatus } from '../services/userService';
import CreateUserForm from '../components/CreateUserForm';
import { AuthContext } from '../context/AuthContext';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Chip,
    Modal
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExcelColumnFilter from '../components/ExcelColumnFilter';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [columnFilters, setColumnFilters] = useState({});
    const { auth } = useContext(AuthContext);

    const fetchUsers = useCallback(async () => {
        try {
            const data = await getUsers();
            setUsers(data.filter(user => user.status === 'active'));
        } catch (err) {
            setError('No se pudieron obtener los usuarios.');
            console.error(err);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleDeleteUser = async (userId) => {
        if (window.confirm('¿Seguro que deseas eliminar este usuario?')) {
            try {
                await updateUserStatus(userId, 'inactive');
                fetchUsers(); // Refresh the list
            } catch (err) {
                setError('No se pudo eliminar el usuario.');
                console.error(err);
            }
        }
    };

    const handleUserCreated = () => {
        setShowCreateForm(false);
        fetchUsers();
    };

    const updateColumnFilter = (column, values) => {
        setColumnFilters((prev) => ({
            ...prev,
            [column]: values
        }));
    };

    const clearFilters = () => setColumnFilters({});

    const getColumnValue = (user, column) => {
        if (column === 'id') {
            return (user.id ?? '').toString();
        }
        if (column === 'username') {
            return user.username || '';
        }
        if (column === 'role') {
            return user.role || '';
        }
        if (column === 'status') {
            return user.status || '';
        }
        return '';
    };

    const matchesColumnFilter = (value, selectedOptions) => {
        if (selectedOptions === undefined) {
            return true;
        }
        return selectedOptions.includes(value);
    };

    const filteredUsers = users.filter((user) => (
        matchesColumnFilter(getColumnValue(user, 'id'), columnFilters.id) &&
        matchesColumnFilter(getColumnValue(user, 'username'), columnFilters.username) &&
        matchesColumnFilter(getColumnValue(user, 'role'), columnFilters.role) &&
        matchesColumnFilter(getColumnValue(user, 'status'), columnFilters.status)
    ));

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Gestión de usuarios
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateForm(true)}
                >
                    Agregar usuario
                </Button>
                <Button variant="text" onClick={clearFilters}>Limpiar filtros</Button>
            </Box>
            {error && <Typography color="error">{error}</Typography>}
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID"
                                    options={users.map((item) => getColumnValue(item, 'id'))}
                                    selectedOptions={columnFilters.id}
                                    onChange={(values) => updateColumnFilter('id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Usuario"
                                    options={users.map((item) => getColumnValue(item, 'username'))}
                                    selectedOptions={columnFilters.username}
                                    onChange={(values) => updateColumnFilter('username', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Rol"
                                    options={users.map((item) => getColumnValue(item, 'role'))}
                                    selectedOptions={columnFilters.role}
                                    onChange={(values) => updateColumnFilter('role', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Estado"
                                    options={users.map((item) => getColumnValue(item, 'status'))}
                                    selectedOptions={columnFilters.status}
                                    onChange={(values) => updateColumnFilter('status', values)}
                                />
                            </TableCell>
                            <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredUsers.map((user) => (
                            <TableRow key={user.id}>
                                <TableCell>{user.id}</TableCell>
                                <TableCell>{user.username}</TableCell>
                                <TableCell>{user.role}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={user.status}
                                        color={user.status === 'active' ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        aria-label="edit"
                                        // onClick={() => navigate(`/dashboard/users/edit/${user.id}`)} // Will add navigation later
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => handleDeleteUser(user.id)}
                                        color="error"
                                        disabled={auth && auth.username === user.username}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Modal
                open={showCreateForm}
                onClose={() => setShowCreateForm(false)}
                aria-labelledby="create-user-modal-title"
                aria-describedby="create-user-modal-description"
            >
                <Box sx={style}>
                    <CreateUserForm onUserCreated={handleUserCreated} />
                </Box>
            </Modal>
        </Paper>
    );
};

export default UserManagement;
